import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import { fetchAdminIntegrationQuotas } from '../../../services/Http/AdminApi';

const AdminQuotas = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const resp = await fetchAdminIntegrationQuotas();
      if (request !== loadRequest.current) return;
      setRows(resp?.data || []);
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load integration quotas');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); return () => { loadRequest.current += 1; }; }, [load]);

  const columns = useMemo(() => ([
    { key: 'organization_name', label: 'Organization' },
    { key: 'integrator_name', label: 'Integrator' },
    { key: 'integration_type', label: 'Type' },
    { key: 'quota_type', label: 'Quota Type' },
    { key: 'limit_value', label: 'Limit' },
    { key: 'current_usage', label: 'Used' },
    { key: 'remaining', label: 'Remaining' },
    { key: 'utilization', label: 'Utilization', render: (r) => <UtilizationBadge limit={r.limit_value} used={r.current_usage} /> },
    { key: 'reset_at', label: 'Reset At', render: (r) => (r.reset_at ? new Date(r.reset_at).toLocaleString() : '—') },
  ]), []);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Integration Quotas</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <AdminFilterBar>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FreshnessBadge status={fetchStatus} sourceType="operational" />
          </div>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </AdminFilterBar>
        {fetchStatus === 'failed' ? <p role="alert">Failed to load integration quotas</p> : <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No quota records found" />}
      </PageContent>
    </PageContainer>
  );
};

export default AdminQuotas;

const UtilizationBadge = ({ limit, used }) => {
  const l = Number(limit || 0);
  const u = Number(used || 0);
  const pct = l > 0 ? Math.round((u / l) * 100) : 0;
  let tone = 'success';
  if (pct >= 90) tone = 'danger';
  else if (pct >= 70) tone = 'warning';
  return <Badge $tone={tone}>{pct}%</Badge>;
};

const Badge = styled.span`
  display:inline-flex; align-items:center; justify-content:center;
  min-width:48px; padding: 0.25rem 0.5rem; border-radius:999px; font-size: 0.75rem; font-weight:700;
  background:${({theme,$tone}) => ({ success: theme.colors.successBg, warning: theme.colors.warningBg, danger: theme.colors.dangerBg }[$tone] || theme.colors.surfaceMuted)};
  color:${({theme,$tone}) => ({ success: theme.colors.success, warning: theme.colors.warning, danger: theme.colors.danger }[$tone] || theme.colors.textMuted)};
`;
